<div class="settings-section">
  <!-- Section header — only visible when subscriptions exist -->
  <ng-container *ngIf="isTenantConfigured && subscriptions.length > 0">
    <div class="section-header-row">
      <div>
        <h3 class="section-heading">VM Subscriptions</h3>
        <p class="section-desc">Manage Azure subscriptions used to provision VMs for this class. Each subscription is
          bound to the region from the attached VM template.</p>
      </div>
      <div class="section-header-actions">
        <button mat-stroked-button (click)="refreshAllQuota()" [disabled]="isRefreshingAll || isLoadingQuota"
          matTooltip="Refresh all quotas from Azure">
          <mat-spinner *ngIf="isRefreshingAll" diameter="16"
            style="display:inline-block;margin-right:4px;"></mat-spinner>
          <mat-icon *ngIf="!isRefreshingAll">refresh</mat-icon>
        </button>
        <button mat-flat-button color="primary" (click)="openAddDialog()">
          <mat-icon>add</mat-icon> Add Subscriptions
        </button>
      </div>
    </div>
  </ng-container>

  <!-- Gate state: Azure tenant not configured -->
  <div *ngIf="!isTenantConfigured" class="vmss-gate-state">
    <mat-icon class="vmss-gate-icon">lock_outline</mat-icon>
    <p class="vmss-gate-title">Azure tenant not configured</p>
    <p class="vmss-gate-sub">
      Set up the Azure service principal before adding VM subscriptions.
    </p>
    <button mat-flat-button color="primary" (click)="goToTenant.emit()">
      <mat-icon>domain</mat-icon> Go to Tenant Settings
    </button>
  </div>

  <!-- Gate state: no subscriptions added yet -->
  <div *ngIf="isTenantConfigured && subscriptions.length === 0 && !isLoadingQuota" class="vmss-gate-state">
    <mat-icon class="vmss-gate-icon vmss-gate-icon--empty">dns</mat-icon>
    <p class="vmss-gate-title">No subscriptions added</p>
    <p class="vmss-gate-sub">
      Add an Azure subscription with available core quota to enable VM provisioning for this class.
    </p>
    <button mat-flat-button color="primary" (click)="openAddDialog()">
      <mat-icon>add</mat-icon> Add Subscription
    </button>
  </div>

  <div *ngIf="subscriptions.length > 0" class="table-container">
    <table mat-table [dataSource]="dataSource" class="sub-table">

      <!-- Display Name -->
      <ng-container matColumnDef="displayName">
        <th mat-header-cell *matHeaderCellDef>Subscription</th>
        <td mat-cell *matCellDef="let row">
          <div class="sub-name-cell">
            <strong>{{ row.displayName || 'Unnamed' }}</strong>
            <span class="sub-location">{{ row.location || '—' }}</span>
          </div>
        </td>
      </ng-container>

      <!-- Subscription ID -->
      <ng-container matColumnDef="subscriptionId">
        <th mat-header-cell *matHeaderCellDef>Subscription ID</th>
        <td mat-cell *matCellDef="let row">
          <span class="mono-text">{{ row.subscriptionId }}</span>
        </td>
      </ng-container>

      <!-- Location -->
      <ng-container matColumnDef="location">
        <th mat-header-cell *matHeaderCellDef>Region</th>
        <td mat-cell *matCellDef="let row">{{ row.location || '—' }}</td>
      </ng-container>

      <!-- Quota -->
      <ng-container matColumnDef="quota">
        <th mat-header-cell *matHeaderCellDef>Quota (Cores)</th>
        <td mat-cell *matCellDef="let row">
          <div *ngIf="row.coresLimit !== undefined; else noQuota" class="quota-cell">
            <div class="quota-numbers">
              <span class="quota-used">{{ row.coresCurrentUsage ?? 0 }}</span>
              <span class="quota-sep">/</span>
              <span class="quota-limit">{{ row.coresLimit }}</span>
              <span class="quota-pct">({{ quotaUsagePercent(row) }}%)</span>
              <mat-icon *ngIf="getFamilyTooltip(row)" class="quota-family-icon" [matTooltip]="getFamilyTooltip(row)"
                matTooltipClass="quota-family-tooltip">info_outline</mat-icon>
              <span *ngIf="row.pendingCores" class="quota-pending" matTooltip="Cores pending provisioning">
                &bull; {{ row.pendingCores }} pending
              </span>
            </div>
            <mat-progress-bar mode="determinate" [value]="quotaUsagePercent(row)"
              [style.--mdc-linear-progress-active-indicator-color]="quotaBarColor(row)"
              [style.--mdc-linear-progress-track-color]="'#e0e0e0'" class="quota-bar">
            </mat-progress-bar>
            <span class="quota-ts">Checked: {{ formatQuotaCheckedAt(row.quotaCheckedAt) }}</span>
          </div>
          <ng-template #noQuota>
            <ng-container *ngIf="row.providerRegistrationStatus === 'registering'; else dashOnly">
              <span class="provider-registering-badge"
                matTooltip="Azure resource providers are being registered for this subscription. Refresh in a few minutes to view quota."
                matTooltipPosition="right">
                <mat-icon class="registering-icon">hourglass_empty</mat-icon>
                Registering providers…
              </span>
            </ng-container>
            <ng-template #dashOnly><span class="text-muted">—</span></ng-template>
          </ng-template>
        </td>
      </ng-container>

      <!-- Optional VMs (max provisionable given quota) -->
      <ng-container matColumnDef="optionalVms">
        <th mat-header-cell *matHeaderCellDef>VMs Available</th>
        <td mat-cell *matCellDef="let row">
          <div *ngIf="row.maxVms !== undefined && row.maxVms !== null; else noMaxVms" class="max-vms-cell">
            <span class="max-vms-badge" [ngClass]="vmBadgeBand(row)">
              <mat-icon class="max-vms-icon">computer</mat-icon>
              {{ row.maxVms }} VM{{ row.maxVms !== 1 ? 's' : '' }}
            </span>
            <span *ngIf="row.vmVCpus" class="max-vms-vcpu">{{ row.vmVCpus }} vCPUs/VM</span>
          </div>
          <ng-template #noMaxVms><span class="text-muted">—</span></ng-template>
        </td>
      </ng-container>

      <!-- Actions -->
      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef></th>
        <td mat-cell *matCellDef="let row">
          <div class="row-actions">
            <button mat-icon-button matTooltip="Refresh quota" [disabled]="row.isRefreshing"
              (click)="refreshQuota(row)">
              <mat-spinner *ngIf="row.isRefreshing" diameter="18"></mat-spinner>
              <mat-icon *ngIf="!row.isRefreshing">sync</mat-icon>
            </button>
            <button mat-icon-button color="warn" [matTooltip]="blockReason(row) ?? 'Remove subscription'"
              [disabled]="row.isRemoving || !!blockReason(row)" (click)="removeSubscription(row)">
              <mat-spinner *ngIf="row.isRemoving" diameter="18"></mat-spinner>
              <mat-icon *ngIf="!row.isRemoving">delete_outline</mat-icon>
            </button>
          </div>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
    </table>
    <mat-paginator [pageSize]="pageSize" [pageSizeOptions]="pageSizeOptions"
      appPersistPageSize="class:vmSubscriptions:list" showFirstLastButtons aria-label="Select page of subscriptions">
    </mat-paginator>
  </div>

</div>